import React, { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';

interface LaunchReelPlayerProps {
  src: string;        // base path without extension, e.g. "/launch-reel-v1"
  poster: string;     // poster image path
  className?: string;
  ariaLabel?: string;
}

const LaunchReelPlayer: React.FC<LaunchReelPlayerProps> = ({ src, poster, className, ariaLabel }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Auto-play muted when scrolled into view, pause when out of view
  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {
            // Autoplay blocked — keep poster visible, user can tap to play
          });
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
    setHasInteracted(true);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    setHasInteracted(true);
  };

  return (
    <div ref={containerRef} className={`relative group ${className ?? ''}`}>
      <video
        ref={videoRef}
        poster={poster}
        muted
        playsInline
        loop
        preload="metadata"
        aria-label={ariaLabel}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onClick={togglePlay}
        className="w-full h-auto block cursor-pointer"
      >
        <source src={`${src}.webm`} type="video/webm" />
        <source src={`${src}.mp4`} type="video/mp4" />
        Seu navegador não suporta vídeo HTML5.
      </video>

      {/* Play overlay — visible when paused */}
      {!isPlaying && (
        <button
          type="button"
          onClick={togglePlay}
          aria-label="Reproduzir vídeo"
          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
        >
          <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-yellow-400 flex items-center justify-center shadow-2xl shadow-yellow-400/40">
            <Play className="w-9 h-9 md:w-11 md:h-11 text-slate-900 ml-1" strokeWidth={2.5} fill="currentColor" />
          </span>
        </button>
      )}

      {/* Mute / unmute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        aria-label={isMuted ? 'Ativar som' : 'Silenciar'}
        className="absolute bottom-4 right-4 md:bottom-6 md:right-6 w-12 h-12 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700/60 flex items-center justify-center text-white hover:bg-slate-800/90 hover:border-yellow-400/40 transition-all opacity-80 hover:opacity-100 z-10"
      >
        {isMuted ? <VolumeX className="w-5 h-5" strokeWidth={2.2} /> : <Volume2 className="w-5 h-5 text-yellow-400" strokeWidth={2.2} />}
      </button>

      {/* Pause toggle — only shown after interaction, on hover */}
      {hasInteracted && isPlaying && (
        <button
          type="button"
          onClick={togglePlay}
          aria-label="Pausar vídeo"
          className="absolute bottom-4 left-4 md:bottom-6 md:left-6 w-12 h-12 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700/60 flex items-center justify-center text-white opacity-0 group-hover:opacity-80 hover:!opacity-100 transition-all z-10"
        >
          <Pause className="w-5 h-5" strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
};

export default LaunchReelPlayer;
